import { FC } from "react";

import {
  GetIssueCountsResponse,
  GetIssueFiltersResponse,
  IssueTab,
  issueTabFromJSON,
  issueTabToJSON,
  IssueType,
} from "@protos/issues/v1/issues";
import {
  FilterChips,
  FilterMultiselect,
  FilterSingleSelect,
  FilterTreeSelect,
  FooterMessage,
} from "@shared/components";
import {
  DEFAULT_BRANCHES_GROUP,
  OTHER_BRANCHES_GROUP,
  REPO_FILTER_PARAM,
  REPO_REF_FILTER_PARAM,
} from "@shared/constants";
import {
  useBranches,
  useProjectBranches,
  useSupportsFeature,
} from "@shared/hooks";

import { DISABLED_FILTER_MESSAGE } from "../../constants/filters";
import { CommonIssueFilterState } from "../../hooks";
import {
  createFilterData,
  formatGroupedFilterValues,
  formatGroupedFilterValuesForFilterParams,
} from "../../utils";

const STATUS_CLASS_NAME_MAP: Record<IssueType, string> = {
  [IssueType.SECRETS]: "",
  [IssueType.SCA]: "",
  [IssueType.SAST]: "findings-tutorial-step-1",
  [IssueType.RESEARCH]: "",
  [IssueType.UNSPECIFIED]: "",
};

interface CommonFiltersProps {
  issueType: IssueType;
  projectBranches: ReturnType<typeof useProjectBranches>;
  findingFilters: CommonIssueFilterState;
  isLoading: boolean;
  hasMatchingFindings: boolean;
  footerMessage?: FooterMessage;
  isDisabledFiltered: (findingFilter: string[]) => boolean;
  issueFilterData?: GetIssueFiltersResponse;
  branches: ReturnType<typeof useBranches>;
  selectableRefs: string[];
  counts?: GetIssueCountsResponse;
}

export const CommonFilters: FC<CommonFiltersProps> = ({
  issueType,
  projectBranches,
  findingFilters,
  isLoading,
  hasMatchingFindings,
  footerMessage,
  isDisabledFiltered,
  issueFilterData,
  branches,
  selectableRefs,
  counts,
}) => {
  const hasProjectRefsFilter = useSupportsFeature("hasProjectRefsFilter");
  const hasProjectLevelRBAC = useSupportsFeature("hasProjectLevelRBAC");

  const hasPrimaryBranchCapabilities = useSupportsFeature(
    "hasPrimaryBranchCapabilities"
  );

  return (
    <>
      {hasProjectRefsFilter ? (
        <>
          <FilterTreeSelect
            label="Project and branches"
            data={projectBranches}
            value={formatGroupedFilterValues({
              repo_ref: findingFilters.repoRef.map(toString),
              repo: findingFilters.repo,
            })}
            onChange={(value) => {
              const formattedValues = formatGroupedFilterValuesForFilterParams(
                value,
                [REPO_FILTER_PARAM, REPO_REF_FILTER_PARAM]
              );
              findingFilters.setRepoRef(
                formattedValues[REPO_REF_FILTER_PARAM].map(parseInt)
              );
              findingFilters.setRepo(formattedValues[REPO_FILTER_PARAM]);
            }}
            placeholder="All projects and branches"
            isLoading={isLoading}
            withTooltips
            disabled={
              !isLoading &&
              !hasMatchingFindings &&
              findingFilters.repo.length === 0 &&
              findingFilters.repoRef.length === 0
            }
            disabledMessage={DISABLED_FILTER_MESSAGE}
            footerMessage={footerMessage}
          />
          <FilterChips
            label="Branch type"
            data={[
              {
                value: DEFAULT_BRANCHES_GROUP,
                label: "Default",
              },
              {
                value: OTHER_BRANCHES_GROUP,
                label: "Other",
              },
            ]}
            multiple
            value={findingFilters.ref}
            onChange={(value) => findingFilters.setRef(value)}
            tooltip={
              <>
                Filters all selected branches by type. If no projects or
                branches are selected, filters branches for all projects.
              </>
            }
            disabled={isDisabledFiltered(findingFilters.ref)}
            disabledMessage={DISABLED_FILTER_MESSAGE}
          />
        </>
      ) : !hasPrimaryBranchCapabilities ? (
        <>
          <FilterMultiselect
            label="Projects"
            data={createFilterData(issueFilterData?.repoNames || [])}
            value={findingFilters.repo}
            onChange={(value) => findingFilters.setRepo(value)}
            placeholder="All projects"
            isLoading={isLoading}
            maxBadges={4}
            disabled={isDisabledFiltered(findingFilters.repo)}
            disabledMessage={DISABLED_FILTER_MESSAGE}
            footerMessage={footerMessage}
          />
          <FilterTreeSelect
            label="Branches"
            data={branches}
            value={selectableRefs}
            onChange={(value) => findingFilters.setRef(value)}
            placeholder="All branches, refs and PRs"
            isLoading={isLoading}
            withTooltips
            disabled={isDisabledFiltered(findingFilters.ref)}
            disabledMessage={DISABLED_FILTER_MESSAGE}
          />
        </>
      ) : null}

      {hasProjectLevelRBAC && (
        <FilterMultiselect
          label="Teams"
          data={createFilterData(issueFilterData?.teamNames || [])}
          value={findingFilters.team}
          onChange={(value) => findingFilters.setTeam(value)}
          placeholder="All teams"
          isLoading={isLoading}
          maxBadges={4}
          disabled={isDisabledFiltered(findingFilters.team)}
          disabledMessage={DISABLED_FILTER_MESSAGE}
        />
      )}

      <FilterMultiselect
        label="Tags"
        data={createFilterData(issueFilterData?.repoTags || [])}
        value={findingFilters.repoTag}
        onChange={(value) => findingFilters.setRepoTag(value)}
        placeholder="All project tags"
        isLoading={isLoading}
        maxBadges={4}
        withTooltips
        disabled={isDisabledFiltered(findingFilters.repoTag)}
        disabledMessage={DISABLED_FILTER_MESSAGE}
      />

      <div className={STATUS_CLASS_NAME_MAP[issueType]}>
        <FilterSingleSelect
          label="Status"
          data={[
            {
              value: issueTabToJSON(IssueTab.OPEN),
              label: counts ? "Open (" + counts.open + ")" : "Open",
            },
            {
              value: issueTabToJSON(IssueTab.REVIEWING),
              label: counts
                ? "Reviewing (" + counts.reviewing + ")"
                : "Reviewing",
            },
            {
              value: issueTabToJSON(IssueTab.FIXING),
              label: counts ? "Fixing (" + counts.fixing + ")" : "Fixing",
            },
            {
              value: issueTabToJSON(IssueTab.IGNORED),
              label: counts ? "Ignored (" + counts.ignored + ")" : "Ignored",
            },
            {
              value: issueTabToJSON(IssueTab.CLOSED),
              label: counts ? "Fixed (" + counts.closed + ")" : "Fixed",
            },
          ]}
          value={findingFilters.tab}
          onChange={(value) => findingFilters.setTab(issueTabFromJSON(value))}
        />
      </div>
    </>
  );
};
